// Iter 130 — QR Clock-in/out page.
// Reached by scanning the site QR (e.g. /clock?site=DAP) OR via a direct link.
// Single big button toggles state. Reads ?site= to scope the lookup to a
// specific site's QR — gives the PM confidence that staff are scanning
// the right one before clocking on.
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, MapPin, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClockPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const siteId = params.get('site') || null;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [lastAction, setLastAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await axios.get(`${API}/roster/clock/status${siteId ? `?site_id=${siteId}` : ''}`,
        { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load clock status');
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await axios.post(`${API}/roster/clock`,
        { site_id: siteId, shift_id: status?.today_shift?.shift_id },
        { withCredentials: true });
      setLastAction(r.data);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not record clock event');
    }
    setBusy(false);
  };

  const state = status?.state || 'not_started';
  const hasShift = !!status?.today_shift;
  const buttonLabel = state === 'not_started'
    ? 'Clock IN'
    : state === 'clocked_in' ? 'Clock OUT' : 'Done for the day';
  const buttonColor = state === 'not_started'
    ? '#15803d' : state === 'clocked_in' ? '#dc2626' : '#94a3b8';
  const buttonDisabled = busy || !hasShift || state === 'clocked_out';

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-start pt-10 px-4" data-testid="clock-page">
      <div className="w-full max-w-md bg-card border rounded-2xl shadow-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-[#0d2644] text-white flex items-center justify-center">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#0d2644' }}>Time clock</h1>
            <div className="text-xs text-muted-foreground">
              {user?.name} · {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}
            </div>
          </div>
        </div>

        {siteId && (
          <div className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#52c3c5]/15 text-[#0d2644]">
            <MapPin className="w-3 h-3" /> Site: {siteId}
          </div>
        )}

        {loading ? (
          <div className="py-10 flex flex-col items-center text-muted-foreground text-sm">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            Loading your shift…
          </div>
        ) : !hasShift ? (
          <div className="rounded-lg border bg-amber-50 border-amber-200 p-4 text-sm" data-testid="clock-no-shift">
            <div className="font-bold text-amber-800 mb-1 inline-flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              No published shift today{siteId ? ` at site ${siteId}` : ''}
            </div>
            <div className="text-amber-700/80 text-xs">
              If this is a mistake, please contact your practice manager.
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1 mb-4" data-testid="clock-shift-card">
              <div className="font-bold" style={{ color: '#0d2644' }}>
                Today&apos;s shift: {status.today_shift.start}–{status.today_shift.end}
              </div>
              {status.today_shift.role_label && (
                <div className="text-xs text-muted-foreground">{status.today_shift.role_label}</div>
              )}
              <div className="text-xs flex items-center gap-4 mt-1.5">
                <div>
                  <span className="text-muted-foreground">Clock IN:</span>{' '}
                  <strong data-testid="clock-actual-in">{status.entry?.actual_start || '—'}</strong>
                </div>
                <div>
                  <span className="text-muted-foreground">Clock OUT:</span>{' '}
                  <strong data-testid="clock-actual-out">{status.entry?.actual_end || '—'}</strong>
                </div>
              </div>
            </div>

            <button onClick={toggle} disabled={buttonDisabled} data-testid="clock-toggle-btn"
                    className="w-full text-white text-2xl font-bold py-6 rounded-2xl shadow-lg transition-transform active:scale-[0.97] disabled:opacity-60 disabled:active:scale-100"
                    style={{ background: buttonColor }}>
              {busy ? <Loader2 className="w-7 h-7 animate-spin mx-auto" /> :
                state === 'clocked_out'
                  ? <CheckCircle2 className="w-7 h-7 mx-auto" />
                  : buttonLabel}
            </button>

            {state === 'clocked_in' && status.entry?.actual_start && (
              <div className="text-center text-xs text-muted-foreground mt-3" data-testid="clock-on-shift-note">
                You clocked in at <strong>{status.entry.actual_start}</strong>. Tap above when you finish.
              </div>
            )}
            {state === 'clocked_out' && (
              <div className="text-center text-xs text-green-700 mt-3" data-testid="clock-done-note">
                ✓ Clocked {status.entry?.actual_start}–{status.entry?.actual_end} today. See you tomorrow!
              </div>
            )}
            {lastAction && state === 'clocked_in' && (
              <div className="text-center text-xs text-muted-foreground mt-2">
                Last action: clocked in at {lastAction.time}.
              </div>
            )}
          </>
        )}

        {err && (
          <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="clock-error">
            {err}
          </div>
        )}

        <div className="mt-5 pt-3 border-t flex items-center justify-between text-xs">
          <Link to="/roster" className="text-muted-foreground hover:text-foreground">← Open roster</Link>
          <Link to="/" className="text-muted-foreground hover:text-foreground">i3 home →</Link>
        </div>
      </div>
    </div>
  );
}
